import React, { useState, useCallback, memo, useContext } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import Modal from 'react-bootstrap/Modal';
import Button from 'components/inputs/Button';
import { roundOfDigit } from 'components/common/utils';
import { NavContext } from 'components/privateRoute/PrivateRoute';
import { MdClose } from 'react-icons/md';
// Memoized components for better performance


const DetailCard = memo(({ title, children, className = '' }) => (
  <div className={`card shadow-sm ${className}`}>
    {title && (
      <div className="card-header bg-transparent">
        <h6 className="mb-0">{title}</h6>
      </div>
    )}
    <div className="card-body">{children}</div>
  </div>
));
DetailCard.displayName = 'DetailCard';

const InfoField = memo(({ label, value }) => (
  <div className="mb-3">
    <small className="text d-block">{label}</small>
    <span className="fw-medium">{value}</span>
  </div>
));
InfoField.displayName = 'InfoField';

const AlertItem = memo(({ alert }) => {
  if (!alert?.alertType) return null;

  const { navigateTo } = useContext(NavContext);
  const [isAltFormat, setIsAltFormat] = useState(false);
  const [modalState, setModalState] = useState({ show: false, view: null });

  // Always use the latest alert data
  const currentAlertData = alert;
  const chargingStationData = currentAlertData?.stationDetails;

  const handleViewDetails = useCallback(
    (type) => {
      setModalState({ show: true, view: type });
    },
    []
  );

  const handleClose = useCallback(() => {
    setModalState({ show: false, view: null });
  }, []);
 
  const renderChargerDetails = useCallback(() => {
    const { chargerDetails } = currentAlertData;
    if (!chargerDetails) return null;
    return (
      <div className="charger-details p-2">
        <div className="row g-4">
          <DetailCard className="border-0">
            <div className="d-flex justify-content-between align-items-center">
              <div style={{ 'cursor': 'pointer' }} onClick={() => navigateTo(`/charger/${chargerDetails?.charger_mongo_id || chargerDetails?._id}`)}><InfoField label="OCPP ID" value={chargerDetails.charger_id} /></div>
              <span
                className={`badge ${chargerDetails.charger_status === 'Available' ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'
                  }`}
              >
                {chargerDetails.charger_status}
              </span>
            </div>
          </DetailCard>

          <DetailCard>
            {[
              {
                label: 'Charging Station',
                value: chargerDetails?.charging_station,
              },
              {
                label: 'Contact Name',
                value: chargingStationData?.contact_name,
              },
              {
                label: 'Address',
                value: chargerDetails?.address,
              },
              {
                label: 'Phone Number',
                value: chargingStationData?.contact_phone && `+${chargingStationData?.countryCode}${chargingStationData?.contact_phone}`,
              },
              {
                label: 'Email Address',
                value: chargingStationData?.contact_email,
              },
            ]
              .filter((field) => field.value)
              .map(({ label, value }) => (
                <InfoField key={label} label={label} value={value} />
              ))}
          </DetailCard>

          <DetailCard title="Connected Plugs">
            <div className="list-group list-group-flush d-flex flex-row justify-content-between flex-wrap">
              {chargerDetails.plugs?.map((plug) => (
                <div key={plug._id} className="list-group-item d-flex gap-4 align-items-center border-0 px-0">
                  <div className="d-flex align-items-center">
                    <div
                      className={`rounded-circle me-2 ${plug.status === 'Available' ? 'bg-success' : 'bg-danger'}`}
                      style={{ width: '8px', height: '8px' }}
                    />
                    <span>{plug.name}</span>
                  </div>
                  <span className={`badge ${plug.status === 'Available' ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'}`}>
                    {plug.status}
                  </span>
                </div>
              ))}
            </div>
          </DetailCard>
        </div>
      </div>
    );
  }, [currentAlertData.chargerDetails, chargingStationData]);

  const renderUserDetails = useCallback(() => {
    const { userDetails } = currentAlertData;
    if (!userDetails) return null;

    return (
      <div className="user-details p-2">
        <div className="row g-4">
          <DetailCard className="border-0">
            <div className="d-flex align-items-center">
              <div
                className="rounded-circle bg-primary d-flex align-items-center justify-content-center text-white"
                style={{ width: '48px', height: '48px' }}
              >
                <span className="fs-4">{userDetails.name ? userDetails.name.charAt(0) : 'G'}</span>
              </div>
              <div className="ms-3">
                <h5 className="mb-1" style={{ 'cursor': 'pointer' }} onClick={() => navigateTo(`/customerProfile/${userDetails.id || userDetails._id}`)} >{userDetails?.name ?? 'Guest User'}</h5>
                <p className="text mb-0">{userDetails.email}</p>
              </div>
            </div>
          </DetailCard>

          <div className="col-md-6">
            <DetailCard title="Contact Information">
              <InfoField label="Phone Number" value={`+${userDetails.country_code || 91}${userDetails.phone}`} />
              <InfoField label="Email Address" value={userDetails.email} />
            </DetailCard>
          </div>
          <div className="col-md-6">
            <DetailCard title="Account Details">
              <InfoField label="Wallet Balance" value={`₹ ${roundOfDigit(userDetails.wallet_balance, 2)}`} />
             
            </DetailCard>
          </div>

          {
            currentAlertData.details &&
            <div className="col-md-12">
              <DetailCard title="Booking Type">
                <InfoField label={`User tried booking via ${Object.keys(currentAlertData.details)[0]}`} value={currentAlertData.details[Object.keys(currentAlertData.details)[0]]} />
              </DetailCard>
            </div>
          }

        </div>
      </div>
    );
  }, [currentAlertData.userDetails]);

  const renderBookingDetails = useCallback(() => {
    const { bookingDetails } = currentAlertData;
    console.log('bookingDetails', bookingDetails);
    if (!bookingDetails) return null;

    const startReading = bookingDetails.meterstart ?? bookingDetails.meterstart ?? 0;
    const stopReading = bookingDetails.meterstop ?? bookingDetails.meterstop ?? 0;

    return (
      <div className="booking-details p-2">
        <div className="row g-4">
          <DetailCard className="border-0">
            <div className="d-flex justify-content-between align-items-center">
              <InfoField label="Booking ID" value={bookingDetails.booking_id || bookingDetails._id || 'N/A'} />
              <span className={`badge ${bookingDetails.status === 'completed' ? 'bg-success' : 'bg-warning'}`}>
                {bookingDetails.status || 'Unknown'}
              </span>
            </div>
          </DetailCard>

          <DetailCard title="Booking Information">
            <div className="row g-3">
              {[
                { 
                  label: 'Schedule Date & Time', 
                  value: bookingDetails.schedule_datetime || bookingDetails.scheduleDatetime
                    ? moment(bookingDetails.schedule_datetime || bookingDetails.scheduleDatetime).format('DD MMM YYYY, h:mm A')
                    : 'N/A'
                },
                { label: 'Transaction ID', value: bookingDetails.transaction_id || bookingDetails.transactionId || 'N/A' },
                { label: 'Connector ID', value: bookingDetails.connector_id || bookingDetails.connectorId || 'N/A' },
                { label: 'ID Tag', value: bookingDetails.id_tag || bookingDetails.idTag || 'N/A' },
                { 
                  label: 'Time Taken', 
                  value: bookingDetails.time_taken || bookingDetails.timeTaken
                    ? `${Math.floor((bookingDetails.time_taken || bookingDetails.timeTaken) / 60000)} minutes` 
                    : 'N/A' 
                },
                { label: 'Stop Reason', value: bookingDetails.stop_reason || bookingDetails.stopReason || 'N/A' },
                ...(currentAlertData.details && Object.keys(currentAlertData.details)[0]) === 'party_id' ? [{
                  label: 'Party ID', value: currentAlertData.details.party_id
                }] : []
              ].map(({ label, value }) => (
                <div key={label} className="col-md-6">
                  <InfoField label={label} value={value} />
                </div>
              ))}
            </div>
          </DetailCard>

          <DetailCard title="Meter Readings">
            <div className="row g-3">
              <div className="col-md-6">
                <div className="p-3 rounded">
                  <InfoField label="Start Reading" value={`${startReading} kWh`} />
                </div>
              </div>
              <div className="col-md-6">
                <div className="p-3 rounded">
                  <InfoField label="Stop Reading" value={`${stopReading} kWh`} />
                </div>
              </div>
            </div>
          </DetailCard>
        </div>
      </div>
    );
  }, [currentAlertData.bookingDetails, currentAlertData.details]);


  return (
    <>
      <div className={`alert-item alert`}>
        <div className="alert-content">
          <span className={'alert-message'}>{alert.message}</span>
          <span
            className="alert-date"
            onClick={() => setIsAltFormat((prev) => !prev)}
            style={{ cursor: 'pointer' }}
            title='Change Timezone'
          >
            {isAltFormat
              ? alert.createdAt.concat(' UTC')
              : moment(alert.createdAt).format('DD/MM/YYYY HH:mm:ss').concat(' IST')
            }
          </span>        </div>
        <div className="alert-btn-modal">
          <Button onClick={() => handleViewDetails('All Details')}>View All Details</Button>
        </div>
      </div>

      <Modal size={currentAlertData?.chargerDetails && currentAlertData?.userDetails ? 'xl' : currentAlertData?.chargerDetails ? 'auto' : 'xl'} show={modalState.show} onHide={handleClose} centered dialogClassName={currentAlertData?.bookingDetails && 'modal-2xl'} className="alert-modal">
        <Modal.Header>
          <div className='alert-modal-header'>
            <Modal.Title>{modalState.view}</Modal.Title>
            <button onClick={handleClose}>
              <MdClose />
            </button>
          </div>
        </Modal.Header>
        <Modal.Body className='alert-modal-content'>
        {modalState.view === 'All Details' && (
            <>
              {currentAlertData?.chargerDetails && (
                <div className="details-section mb-4">
                  <h5 className="section-heading mb-3">Charger Details</h5>
                  {renderChargerDetails()}
                </div>
              )}
              {currentAlertData?.userDetails && (
                <div className="details-section mb-4">
                  <h5 className="section-heading mb-3">User Details</h5>
                  {renderUserDetails()}
                </div>
              )}
              {currentAlertData?.bookingDetails && (
                <div className="details-section">
                  <h5 className="section-heading">Booking Details</h5>
                  {renderBookingDetails()}
                </div>
              )}
            </>
          )}

        </Modal.Body>
      </Modal>
    </>
  );
});
AlertItem.displayName = 'AlertItem';

AlertItem.propTypes = {
  alert: PropTypes.shape({
    message: PropTypes.string.isRequired,
    alertType: PropTypes.string.isRequired,
    createdAt: PropTypes.string.isRequired,
    color: PropTypes.string,
    chargerDetails: PropTypes.object,
    userDetails: PropTypes.object,
    bookingDetails: PropTypes.object,
    stationDetails: PropTypes.object,
    userId: PropTypes.string,
    details: PropTypes.object
  }).isRequired,
};

DetailCard.propTypes = {
  title: PropTypes.string,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

InfoField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
};

export default AlertItem;